import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNotificationSocket } from "@/hooks/useNotifications";

import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

const COLLAPSED_KEY = "kms-sidebar-collapsed";

/**
 * Shell wrapping every authenticated route.
 *
 * Layout owns two pieces of UI chrome state:
 *   - `collapsed` — desktop rail vs full panel; persisted to localStorage.
 *   - `mobileOpen` — drawer state on small screens; ephemeral, auto-closes
 *     on route changes so a tapped link doesn't leave the drawer hanging.
 *
 * RequireAuth guarantees `user` is non-null by the time this renders,
 * so the early return is just a TS-narrowing safety net.
 */
export function AppLayout() {
  const { data: user } = useCurrentUser();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Persist desktop collapse preference.
  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Auto-close the mobile drawer whenever the route changes — a user
  // who tapped a nav link should land on the new page with the drawer
  // already out of the way.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Live notification push for the whole authenticated session.
  useNotificationSocket(!!user);

  if (!user) return null;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background">
      <Sidebar
        user={user}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          user={user}
          onMobileMenuClick={() => setMobileOpen(true)}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
